import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEmailStore } from '../../stores/email-store';
import { useTodoStore } from '../../stores/todo-store';
import { useAppStore } from '../../stores/appStore';
import { useAuthStore } from '../../stores/authStore';
import { askApi, type AskSource } from '../../lib/ask-api';
import { BriefingCard } from '../pa/BriefingCard';
import { PAMessage } from '../pa/PAMessage';
import { PAText } from '../pa/PAText';
import { Citation } from '../pa/Citation';
import { TimeDivider } from '../today/TimeDivider';
import { EmailInlineCard } from '../today/EmailInlineCard';
import { TodoInlineCard } from '../today/TodoInlineCard';
import { Composer } from '../today/Composer';

/**
 * Today - the unified home. Morning briefing card at top, then a scrollable
 * conversation timeline (PA picks up priority items, user asks follow-ups,
 * PA streams answers back with source citations), composer sticky at bottom.
 *
 * Replaces the old split between PAChatView and the focus-cards TodayView.
 */

type ChatRole = 'pa' | 'user';

interface ChatMessage {
  id: string;
  role: ChatRole;
  kind?: 'prose' | 'emails-digest' | 'todos-digest';
  content: React.ReactNode;
  streamingText?: string;
  isStreaming?: boolean;
  sources?: AskSource[];
  error?: string;
}

export function TodayView() {
  const emails = useEmailStore((s) => s.emails);
  const todos = useTodoStore((s) => s.todos);
  const token = useAuthStore((s) => s.token);
  const setActiveEmailId = useAppStore((s) => s.setActiveEmailId);
  const setActiveTodoId = useAppStore((s) => s.setActiveTodoId);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const isDemo = !token || token === 'demo-token';

  // Top emails worth flagging in the "this morning" turn
  const priorityEmails = useMemo(() => {
    return [...emails]
      .filter((e) => !e.isRead && !e.isArchived)
      .sort((a, b) => {
        const ua = isUrgent(a) ? 1 : 0;
        const ub = isUrgent(b) ? 1 : 0;
        if (ua !== ub) return ub - ua;
        return new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime();
      })
      .slice(0, 2);
  }, [emails]);

  // Today's todos worth mentioning
  const todaysTodos = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return todos.filter((t) => {
      if (t.completedAt) return false;
      if (!t.dueDate) return false;
      const d = new Date(t.dueDate);
      d.setHours(0, 0, 0, 0);
      return d.getTime() === today.getTime();
    });
  }, [todos]);

  // Seed conversation - run once on mount when data is available.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (priorityEmails.length === 0 && todaysTodos.length === 0) return;
    seededRef.current = true;

    const seed: ChatMessage[] = [];
    if (priorityEmails.length > 0) {
      seed.push({
        id: 'seed-emails',
        role: 'pa',
        kind: 'emails-digest',
        content: (
          <>
            <div style={{ marginBottom: 10 }}>
              {priorityEmails.length === 1
                ? 'One email came in worth your attention before anything else:'
                : `${priorityEmails.length} emails came in worth your attention before anything else:`}
            </div>
            {priorityEmails.map((e, i) => (
              <React.Fragment key={e.id}>
                <EmailInlineCard email={e} onOpen={() => setActiveEmailId(e.id)} />
                {i < priorityEmails.length - 1 && <div style={{ height: 10 }} />}
              </React.Fragment>
            ))}
          </>
        ),
      });
    }
    if (todaysTodos.length > 0) {
      seed.push({
        id: 'seed-todos',
        role: 'pa',
        kind: 'todos-digest',
        content: (
          <>
            Your todos for today:
            <div style={{ marginTop: 12 }}>
              {todaysTodos.map((t) => (
                <TodoInlineCard
                  key={t.id}
                  todo={t}
                  onOpen={() => setActiveTodoId(t.id)}
                />
              ))}
            </div>
          </>
        ),
      });
    }
    setMessages(seed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priorityEmails.length, todaysTodos.length]);

  // Autoscroll on streaming content
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const submit = useCallback(async () => {
    const q = draft.trim();
    if (!q || isStreaming || q.length < 2 || q.length > 500) return;

    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: q,
    };
    const paId = `p-${Date.now()}`;
    const paMsg: ChatMessage = {
      id: paId,
      role: 'pa',
      content: null,
      streamingText: '',
      isStreaming: true,
    };

    setMessages((prev) => [...prev, userMsg, paMsg]);
    setDraft('');
    setIsStreaming(true);

    const appendToken = (tok: string) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === paId ? { ...m, streamingText: (m.streamingText ?? '') + tok } : m
        )
      );
    };
    const setSources = (sources: AskSource[]) => {
      setMessages((prev) => prev.map((m) => (m.id === paId ? { ...m, sources } : m)));
    };

    try {
      if (isDemo) {
        await streamDemoResponse(q, appendToken);
      } else {
        for await (const event of askApi.streamAsk(q)) {
          if (event.content) appendToken(event.content);
          if (event.sources) setSources(event.sources);
        }
      }
      setMessages((prev) =>
        prev.map((m) => (m.id === paId ? { ...m, isStreaming: false } : m))
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong';
      setMessages((prev) =>
        prev.map((m) =>
          m.id === paId ? { ...m, isStreaming: false, error: message } : m
        )
      );
    } finally {
      setIsStreaming(false);
      inputRef.current?.focus();
    }
  }, [draft, isStreaming, isDemo]);

  return (
    <div
      ref={scrollRef}
      style={{
        flex: 1,
        overflowY: 'auto',
        minHeight: 0,
        minWidth: 0,
      }}
    >
      <div
        style={{
          maxWidth: 720,
          margin: '0 auto',
          padding: '8px 36px 40px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
        }}
      >
        <BriefingCard />

        {messages.length > 0 && <TimeDivider label="This morning" />}

        {messages.map((m) => renderMessage(m))}

        <Composer
          value={draft}
          onChange={setDraft}
          onSubmit={submit}
          disabled={isStreaming}
          placeholder={
            isDemo
              ? 'Demo mode - try: "What did Sarah send?"'
              : 'Ask, capture, or give me a task...'
          }
          inputRef={inputRef}
        />
      </div>
    </div>
  );
}

// Message rendering

function renderMessage(m: ChatMessage): React.ReactNode {
  if (m.role === 'user') {
    return (
      <PAMessage key={m.id} variant="user">
        {m.content}
      </PAMessage>
    );
  }

  return (
    <PAMessage key={m.id} thinking={m.isStreaming && !m.streamingText}>
      {m.kind ? (
        m.content
      ) : (
        <>
          {m.streamingText ? (
            <span style={{ whiteSpace: 'pre-wrap' }}>
              <PAText>{m.streamingText}</PAText>
              {m.isStreaming && <BlinkingCursor />}
            </span>
          ) : m.isStreaming ? null : (
            m.content
          )}
          {m.sources && m.sources.length > 0 && (
            <div style={{ marginTop: 10, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {m.sources.slice(0, 5).map((s) => (
                <Citation
                  key={s.id}
                  source={citationSourceFromCollection(s.collection)}
                  label={s.title}
                  title={s.snippet}
                />
              ))}
            </div>
          )}
          {m.error && (
            <div
              style={{
                marginTop: 10,
                fontSize: 12,
                color: 'var(--error)',
                background: 'color-mix(in srgb, var(--error) 10%, transparent)',
                padding: '8px 10px',
                borderRadius: 8,
              }}
            >
              {m.error}
            </div>
          )}
        </>
      )}
    </PAMessage>
  );
}

function citationSourceFromCollection(c: string): 'email' | 'todo' | 'note' | 'memory' {
  if (c.includes('email')) return 'email';
  if (c.includes('todo')) return 'todo';
  if (c.includes('note')) return 'note';
  return 'memory';
}

function BlinkingCursor() {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: 2,
        height: '1em',
        verticalAlign: 'text-bottom',
        marginLeft: 2,
        background: 'var(--ink-soft)',
        animation: 'blink 1s steps(2) infinite',
      }}
    />
  );
}

// Heuristics + demo stream

function isUrgent(email: { subject: string; snippet: string; isStarred: boolean }): boolean {
  if (email.isStarred) return true;
  const text = `${email.subject} ${email.snippet}`.toLowerCase();
  return /\b(urgent|asap|eod|eow|tomorrow|today|by\s+end|deadline)\b/.test(text);
}

async function streamDemoResponse(
  question: string,
  onToken: (t: string) => void
): Promise<void> {
  const canned = demoResponseFor(question);
  for (const word of canned.split(/(\s+)/)) {
    await new Promise((r) => setTimeout(r, 25));
    onToken(word);
  }
}

function demoResponseFor(q: string): string {
  const lower = q.toLowerCase();
  if (lower.includes('sarah')) {
    return "Sarah Chen sent the Q2 strategy deck update with new market analysis and revised timelines. She is waiting on your review before the Friday leadership sync - this is the top item on your plate today.";
  }
  if (lower.includes('today') || lower.includes('priority')) {
    return 'Three things need you today: reply to Sarah about the Q2 deck, the Q1 report is overdue, and you have 2 high-priority tasks due today. Start with Sarah - she is the most time-sensitive.';
  }
  if (lower.includes('invoice') || lower.includes('stripe')) {
    return 'Stripe sent a $2,499 receipt from Acme Corp. I tagged it as auto-transactional so I did not flag it for action. Want me to log it to expenses?';
  }
  if (lower.includes('jun') || lower.includes('versioning')) {
    return "Jun is blocking on your API versioning call by EOD. The team is split on v2 versus deprecation; shipping Monday depends on your decision.";
  }
  return 'I am running in demo mode so I cannot reach your live data. In production I would stream an answer with source citations from your email, todos, and notes.';
}
